import type {
  CreateUploadSessionRequest,
  CreateUploadSessionResponse,
  UploadedFileMetadata,
} from "@kushya/shared";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api";

export type {
  CreateUploadSessionRequest,
  CreateUploadSessionResponse,
  UploadedFileMetadata,
} from "@kushya/shared";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const extractErrorMessage = (payload: unknown): string | undefined => {
  if (!isRecord(payload)) {
    return undefined;
  }

  const error = payload.error;

  if (typeof error === "string") {
    return error;
  }

  if (isRecord(error) && typeof error.message === "string") {
    return error.message;
  }

  if (typeof payload.message === "string") {
    return payload.message;
  }

  return undefined;
};

const unwrapApiResponse = <T>(payload: unknown): T | undefined => {
  if (!isRecord(payload)) {
    return undefined;
  }

  if (payload.success === true && "data" in payload) {
    return payload.data as T;
  }

  return undefined;
};

export interface UploadOptions {
  onProgress?: (progress: number) => void;
  sessionToken?: string;
}

export async function createUploadSession(
  input: CreateUploadSessionRequest,
): Promise<string> {
  const response = await fetch(`${API_BASE_URL}/upload/session`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(input),
  });

  let data: unknown;
  try {
    data = await response.json();
  } catch {
    data = undefined;
  }

  if (!response.ok) {
    throw new Error(
      extractErrorMessage(data) ??
        "Failed to start upload session. Please try again.",
    );
  }

  const directToken =
    isRecord(data) && typeof data.sessionToken === "string"
      ? data.sessionToken
      : undefined;
  if (directToken) {
    return directToken;
  }

  const wrapped = unwrapApiResponse<CreateUploadSessionResponse>(data);
  if (wrapped?.sessionToken) {
    return wrapped.sessionToken;
  }

  throw new Error(
    "Upload session could not be created. Please refresh and try again.",
  );
}

export async function uploadFile(
  file: File,
  options?: UploadOptions,
): Promise<UploadedFileMetadata> {
  const { onProgress, sessionToken } = options ?? {};
  const formData = new FormData();
  formData.append("file", file);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress(percent);
      }
    });

    xhr.addEventListener("load", () => {
      try {
        const payload = JSON.parse(xhr.responseText);

        if (xhr.status >= 200 && xhr.status < 300) {
          const wrapped = unwrapApiResponse<UploadedFileMetadata>(payload);
          resolve((wrapped ?? payload) as UploadedFileMetadata);
        } else {
          const message = extractErrorMessage(payload) || "Upload failed";
          reject(new Error(message));
        }
      } catch {
        reject(new Error("Invalid server response"));
      }
    });

    xhr.addEventListener("error", () => {
      reject(new Error("Network error during upload"));
    });

    xhr.open("POST", `${API_BASE_URL}/upload`);

    // Include session token if available
    if (sessionToken) {
      xhr.setRequestHeader("X-Session-Token", sessionToken);
    }

    xhr.send(formData);
  });
}
